/**
 * TranslateDropdown — 47-language switcher. On pick, fetches the
 * translation from /api/newspulse/v1/events/:id/translate and swaps
 * headline/summary/story_html on the parent via `onTranslated(payload)`.
 */
import { useEffect, useRef, useState } from "react";
import { Languages, ChevronDown, Loader2, Check } from "lucide-react";
import { npApi } from "@/pages/newspulse/api";

export default function TranslateDropdown({ eventId, currentLang = "en", onTranslated, className = "" }) {
  const [open, setOpen] = useState(false);
  const [langs, setLangs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const wrap = useRef(null);

  useEffect(() => {
    npApi.languages().then((d) => setLangs(d.languages || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const pick = async (lang) => {
    if (lang === currentLang) { setOpen(false); return; }
    setBusy(lang); setErr(null);
    try {
      const tx = await npApi.translateEvent(eventId, lang);
      onTranslated && onTranslated(tx);
      setOpen(false);
    } catch (e) {
      setErr(e.message || "Translation failed");
    } finally {
      setBusy(null);
    }
  };

  const activeLabel = langs.find((l) => l.code === currentLang)?.label || currentLang.toUpperCase();

  return (
    <div className={`relative inline-block ${className}`} ref={wrap} data-testid="translate-dropdown">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors"
              data-testid="translate-dropdown-toggle">
        <Languages className="w-4 h-4" />
        {activeLabel}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-64 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-auto"
             data-testid="translate-dropdown-menu">
          {err && <div className="px-3 py-2 text-xs text-rose-700 border-b border-rose-100 bg-rose-50">{err}</div>}
          {langs.map((l) => (
            <button key={l.code} type="button" onClick={() => pick(l.code)} disabled={busy === l.code}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                      l.code === currentLang ? "bg-blue-50 text-blue-800" : "text-slate-700"
                    }`}
                    data-testid={`translate-dropdown-${l.code}`}>
              {busy === l.code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                l.code === currentLang ? <Check className="w-3.5 h-3.5 text-blue-600" /> :
                <span className="w-3.5 h-3.5" />}
              <span className="flex-1">
                <span className="font-medium">{l.label}</span>
                <span className="text-slate-500"> · {l.native}</span>
              </span>
              <span className="text-[10px] uppercase text-slate-400">{l.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
